import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Award, Factory, HandshakeIcon, MapPin, Phone, Mail, Sparkles, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { BRAND, IMAGES } from "@/lib/data";

const CREDENTIALS = [
  { icon: ShieldCheck, k: "Registered company", v: "ZEBO India Private Limited · incorporated under the Companies Act, 2013 · GST-compliant" },
  { icon: Factory,     k: "In-house factory",   v: "Modular kitchens & wardrobes made in our own facility — no vendor guesswork" },
  { icon: HandshakeIcon, k: "One accountable studio", v: "Single project manager owns design → manufacturing → install → handover" },
  { icon: Award,       k: "Written warranty",    v: "10-yr on modular carcass · 5-yr on hardware · 1-yr on civil finishing" },
];

const BRANDS = {
  "Modular hardware": ["Hettich", "Blum", "Hafele", "Ebco"],
  "Laminates & veneers": ["Merino", "Greenlam", "Century Ply", "Kitply"],
  "Sanitaryware & fittings": ["Kohler", "Jaquar", "Grohe", "Cera"],
  "Lighting": ["Philips", "Havells", "Wipro", "Osram"],
  "Surfaces": ["Kajaria", "Somany", "Nitco", "Asian Granito"],
  "Home appliances": ["Bosch", "Siemens", "Elica", "Faber"],
};

const NUMBERS = [
  { n: "200+", l: "Homes & spaces designed" },
  { n: "45+",  l: "Turnkey commercial fit-outs" },
  { n: "24h",  l: "Callback promise" },
  { n: "5",    l: "Kolkata micro-markets served" },
];

export default function About() {
  return (
    <main data-testid="about-page" className="relative z-10">
      <Seo
        title="About ZEBO India Private Limited — Credentials, Warranty & Brands"
        description="ZEBO is a registered interior design company in Kolkata (Rajdanga). Learn about our incorporation, warranty policy, hardware brands, and why families & businesses trust us."
        path="/about"
        image={IMAGES.living1}
      />

      {/* Hero */}
      <section className="relative min-h-[75vh] flex items-end pt-32 pb-16 sm:pb-24 bg-[#143D63] overflow-hidden">
        <img src={IMAGES.living1} alt="ZEBO studio Kolkata" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/40 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 w-full">
          <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="overline text-[#F8C21C] mb-4">About ZEBO</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="font-serif text-5xl sm:text-7xl lg:text-[86px] font-light text-[#F9F8F6] tracking-tight leading-[0.94] max-w-5xl">
            The <em className="text-[#F8C21C] not-italic">quiet standard</em> for premium interiors in Kolkata.
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.5 }}
            className="text-[#F9F8F6]/80 mt-7 max-w-2xl text-base sm:text-lg leading-relaxed">
            A registered interior design company, a factory of our own, and a promise on paper — this is who you work with when you hire ZEBO.
          </motion.p>
        </div>
      </section>

      {/* Numbers */}
      <section className="border-y border-[#143D63]/10 bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-2 md:grid-cols-4">
          {NUMBERS.map((n) => (
            <div key={n.l} className="p-8 md:p-10 border-r border-[#143D63]/10 last:border-r-0 text-center">
              <p className="font-serif text-4xl sm:text-5xl text-[#143D63] font-light">{n.n}</p>
              <p className="text-xs text-[#143D63]/60 mt-2 uppercase tracking-wider">{n.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Credentials */}
      <section data-testid="about-credentials" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-28">
        <Reveal>
          <p className="overline text-[#8B0000] mb-3">Credentials</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-[#143D63] leading-tight max-w-3xl">
            The receipts, not the rhetoric.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-px bg-[#143D63]/15 border border-[#143D63]/15 mt-12">
          {CREDENTIALS.map((c, i) => (
            <Reveal key={c.k} delay={i * 0.05} className="bg-[#F9F8F6]">
              <div className="p-8 sm:p-10">
                <c.icon className="text-[#8B0000] mb-5" size={28} />
                <h3 className="font-serif text-2xl text-[#143D63] mb-3">{c.k}</h3>
                <p className="text-sm text-[#143D63]/70 leading-relaxed">{c.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Warranty */}
      <section className="bg-[#143D63] py-24 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="overline text-[#F8C21C] mb-3">Written warranty</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-[#F9F8F6] leading-tight">
              Promises we&apos;ll put in writing.
            </h2>
            <p className="text-[#F9F8F6]/70 mt-6 leading-relaxed">
              Every ZEBO project ships with a signed project agreement + warranty card. If something is off, we come back.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.15}>
            <ul className="space-y-4">
              {[
                "10-year warranty on modular carcass & panels",
                "5-year warranty on Hettich / Blum / Hafele hardware",
                "1-year warranty on civil finishing & painting",
                "6-month servicing check-in after handover",
                "Named single project manager for the entire delivery",
              ].map((r) => (
                <li key={r} className="flex items-start gap-3 text-[#F9F8F6]/85">
                  <CheckCircle2 size={20} className="text-[#F8C21C] mt-0.5 shrink-0" /> {r}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Brands */}
      <section data-testid="about-brands" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-28">
        <Reveal>
          <p className="overline text-[#8B0000] mb-3">Brands we specify</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-[#143D63] leading-tight max-w-3xl">
            The label matters. So do the ones on the inside.
          </h2>
          <p className="text-[#143D63]/70 mt-5 max-w-2xl leading-relaxed">
            We only recommend brands we&apos;ve installed, serviced and stood behind. Every project agreement lists the exact SKU going into your home or office.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {Object.entries(BRANDS).map(([category, brands], i) => (
            <Reveal key={category} delay={i * 0.05}>
              <div className="border border-[#143D63]/15 bg-white p-6 h-full">
                <p className="overline text-[#F8C21C] mb-3">{category}</p>
                <div className="flex flex-wrap gap-2">
                  {brands.map((b) => (
                    <span key={b} className="border border-[#143D63]/20 text-[#143D63] px-3 py-1 text-xs font-semibold">{b}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="text-xs text-[#143D63]/50 mt-8">Brand list indicative. Final specs vary by project scope and budget tier.</p>
      </section>

      {/* Studio + CTA */}
      <section className="bg-[#F9F8F6] border-t border-[#143D63]/10 py-24 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <p className="overline text-[#8B0000] mb-3">Visit the studio</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-[#143D63] leading-tight">
              Come see it in person.
            </h2>
            <p className="text-[#143D63]/70 mt-5 leading-relaxed max-w-xl">
              Our Rajdanga studio is open Mon–Sat. Walk in, touch the finishes, meet the team. Book a slot below or call us.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/consultation" data-testid="about-cta"
                className="group inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-8 py-4 font-bold text-sm hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors">
                Book a studio visit
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link to="/guides" data-testid="about-guides"
                className="inline-flex items-center gap-2 border border-[#143D63] text-[#143D63] px-8 py-4 font-bold text-sm hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors">
                <Sparkles size={16} className="text-[#8B0000]" /> Download planning guides
              </Link>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.15}>
            <div className="border border-[#143D63]/15 bg-white p-7 space-y-4">
              <p className="flex items-start gap-2 text-sm text-[#143D63]"><MapPin size={16} className="text-[#8B0000] mt-1 shrink-0" /> {BRAND.address}</p>
              <p className="flex items-center gap-2 text-sm text-[#143D63]"><Phone size={16} className="text-[#8B0000] shrink-0" /> <a href={`tel:${BRAND.phoneRaw}`} className="link-underline">{BRAND.phone}</a></p>
              <p className="flex items-center gap-2 text-sm text-[#143D63]"><Mail size={16} className="text-[#8B0000] shrink-0" /> <a href={`mailto:${BRAND.email}`} className="link-underline">{BRAND.email}</a></p>
              <p className="text-xs text-[#143D63]/50 pt-2 border-t border-[#143D63]/10">Mon–Sat · 10:00–19:00 · Sundays by appointment</p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
